<template>
  <div class="contacts-page row">
    <div class="left-wrap medium-12">
      <h3 v-if="isInitializing" class="loading">{{ $t('CRM_MGMT.LOADING') }}</h3>
      <template v-else>
        <kanban-header
          :header-title="pageTitle"
          :meta="kanbanMeta"
          :initial-kanban-id="kanbanId"
          @update-kanban-selected="handleKanbanSelected"
          @redirect-kanban-page="redirectKanbanPage"
          @ticket-created="reloadTickets"
          @advanced-filters-changed="handleAdvancedFiltersChange"
          @enter-selection-mode="onEnterSelectionMode"
        />
        <h3 v-if="loading" class="loading">{{ this.$t('CRM_MGMT.LOADING') }}</h3>
        <task-board
          v-if="!loading"
          :columns="columns"
          :kanban-id="kanbanId"
          :filters="activeFilters"
          :enter-selection-mode="selectionModeActive"
          @reload-ticket-board="reloadTickets"
          @exit-selection-mode="selectionModeActive = false"
        />
      </template>
    </div>

    <modal-update-ticket-contact
      v-if="showTicketModal"
      :key="`ticket-modal-${selectedTicket.id}`"
      :show="showTicketModal"
      :ticket-selected="selectedTicket"
      @cancel="closeTicketModal"
      @updateTicket="onTicketUpdated"
    />
  </div>
</template>

<script>
import { mapGetters } from 'vuex';

import KanbanHeader from './Header';
import TaskBoard from './Kanban/TaskBoard.vue';
import ModalUpdateTicketContact from '../../conversation/contact/ModalUpdateTicketContact.vue';
import alertMixin from 'shared/mixins/alertMixin';
import { canAccessCRM } from 'dashboard/helper/crmPermissionHelper';
import router from '../../..';
import { frontendURL } from '../../../../helper/URLHelper';

export default {
  components: {
    KanbanHeader,
    TaskBoard,
    ModalUpdateTicketContact,
  },
  mixins: [alertMixin],

  props: {
    ticketId: {
      type: [String, Number],
      default: null,
    },
  },

  data() {
    return {
      kanbanId: 0,
      isInitializing: true,
      loading: false,
      kanbanMeta: {},
      statusFilter: ['open'],
      searchQuery: '',
      dateField: 'created_at',
      startDate: '',
      endDate: '',
      agentFilter: [],
      priorityFilter: [],
      labelFilter: [],
      selectionModeActive: false,
      ticketRequestId: 0,
      selectedTicket: null,
      ticketFetchRequestId: 0,
    };
  },

  computed: {
    ...mapGetters({
      currentUser: 'getCurrentUser',
      accountId: 'getCurrentAccountId',
      kanbanList: 'getKanbanfunnels',
      currentAccountId: 'getCurrentAccountId',
      getAccount: 'accounts/getAccount',
      getTicketSalueValueChange: 'getTicketSalueValueChange',
      tickets: 'getTickets',
      uiSettings: 'getUISettings',
    }),
    columns() {
      return this.tickets;
    },

    kanbanListActive() {
      const listActive = this.kanbanList.filter(kanban => kanban.active);
      const isAdminOrSuperAdmin =
        this.currentUser?.type === 'SuperAdmin' ||
        this.currentUser?.type_role === 'administrator';
      return isAdminOrSuperAdmin
        ? listActive
        : listActive.filter(kanban =>
            kanban?.agents_ids?.includes(this.currentUser.id)
          );
    },
    pageTitle() {
      return this.$t('CRM_PAGE.HEADER.TITLE');
    },

    activeFilters() {
      return {
        status: this.statusFilter,
        q: this.searchQuery || null,
        date_field: this.dateField,
        agent_ids: this.agentFilter,
        start_date: this.startDate || null,
        end_date: this.endDate || null,
        priority: this.priorityFilter,
        label_ids: this.labelFilter.length > 0 ? this.labelFilter : null,
      };
    },

    resolvedTicketId() {
      return this.ticketId || this.$route.params.ticketId || null;
    },

    showTicketModal() {
      return Boolean(this.resolvedTicketId) && Boolean(this.selectedTicket);
    },
  },

  watch: {
    getTicketSalueValueChange(newVal) {
      if (newVal) {
        const oldValue = Number(newVal.old) || 0;
        const newValue = Number(newVal.new) || 0;
        this.kanbanMeta.total_sales_value =
          Number(this.kanbanMeta?.total_sales_value) || 0;

        this.kanbanMeta.total_sales_value =
          this.kanbanMeta.total_sales_value - oldValue + newValue;

        const idColumn = newVal?.ticket?.columns_id;
        const targetColumn = this.columns?.find(
          col => col.column.id === idColumn
        );

        if (targetColumn) {
          targetColumn.column.meta.total_sales_value =
            Number(targetColumn.column.meta.total_sales_value || 0) -
            oldValue +
            newValue;
        }
      }
    },

    resolvedTicketId: {
      immediate: true,
      handler(id) {
        this.resolveSelectedTicket(id);
      },
    },

    loading(isLoading) {
      if (!isLoading && this.resolvedTicketId) {
        this.resolveSelectedTicket(this.resolvedTicketId);
      }
    },

    tickets() {
      if (!this.resolvedTicketId) return;
      const fromBoard = this.findTicketInBoard(this.resolvedTicketId);
      if (fromBoard) {
        this.selectedTicket = { ...fromBoard };
      }
    },
  },
  mounted() {
    this.$store.dispatch('agents/get');
    this.initializeCRM();
    this.$store.dispatch('labelsCrm/get');
    this.validatePermission();
  },
  methods: {
    async initializeCRM() {
      await this.validatePermission();
      this.initializeKanban();
      this.isInitializing = false;
      if (this.resolvedTicketId) {
        this.resolveSelectedTicket(this.resolvedTicketId);
      }
    },

    redirectKanbanPage() {
      const path = frontendURL(`accounts/${this.accountId}/settings/crm`);
      router.push({ path });
    },
    async validatePermission() {
      await this.$store.dispatch('accounts/get');
      await this.$store.dispatch('fetchKanbanfunnels');

      const account = this.getAccount(this.currentAccountId);
      if (account?.activate_companies) {
        await this.$store.dispatch('companies/get');
      }

      if (!canAccessCRM(account, this.currentUser, this.kanbanList)) {
        const path = frontendURL(`accounts/${this.accountId}/conversation`);
        router.push({ path });
      }
    },

    handleKanbanSelected(selectedKanban) {
      const isSameKanban = this.kanbanId === selectedKanban;
      this.selectionModeActive = false;
      this.kanbanId = selectedKanban;
      this.statusFilter = ['open'];
      this.searchQuery = '';
      this.dateField = 'created_at';
      this.startDate = '';
      this.endDate = '';
      this.agentFilter = [];
      this.priorityFilter = [];
      this.labelFilter = [];
      if (!isSameKanban) {
        this.getTicketsDefault(selectedKanban);
      }
    },

    getInitialKanbanId() {
      const savedKanbanIds = this.uiSettings?.lastSelectedKanbanIds || {};
      const savedKanbanId = savedKanbanIds[this.accountId];

      if (savedKanbanId) {
        const kanbanExists = this.kanbanListActive.some(
          kanban => kanban.id === savedKanbanId
        );
        if (kanbanExists) return savedKanbanId;
      }

      return this.kanbanListActive[0]?.id || 0;
    },

    initializeKanban() {
      const initialKanbanId = this.getInitialKanbanId();
      if (initialKanbanId) {
        this.kanbanId = initialKanbanId;
        this.getTicketsDefault(initialKanbanId);
      }
    },

    handleAdvancedFiltersChange({
      searchQuery,
      dateField,
      status,
      startDate,
      endDate,
      agentIds,
      priority,
      labelIds,
    }) {
      this.searchQuery = searchQuery || '';
      this.dateField = dateField || 'created_at';
      this.statusFilter = status && status.length > 0 ? status : ['open'];
      this.startDate = startDate;
      this.endDate = endDate;
      this.agentFilter = agentIds || [];
      this.priorityFilter = priority || [];
      this.labelFilter = labelIds || [];
      if (this.kanbanId) {
        this.getTicketsDefault(this.kanbanId);
      }
    },

    reloadTickets() {
      if (this.kanbanId) {
        this.getTicketsDefault(this.kanbanId);
      }
    },

    onEnterSelectionMode() {
      this.selectionModeActive = true;
    },

    findTicketInBoard(ticketId) {
      const id = Number(ticketId);
      if (!id) return null;

      for (const col of this.columns || []) {
        const ticket = (col.column?.tickets || []).find(
          item => Number(item.id) === id
        );
        if (ticket) return ticket;
      }
      return null;
    },

    async resolveSelectedTicket(ticketId) {
      if (!ticketId) {
        this.selectedTicket = null;
        return;
      }

      const fromBoard = this.findTicketInBoard(ticketId);
      if (fromBoard) {
        this.selectedTicket = { ...fromBoard };
        return;
      }

      if (this.isInitializing || this.loading) {
        return;
      }

      if (
        this.selectedTicket &&
        Number(this.selectedTicket.id) === Number(ticketId)
      ) {
        return;
      }

      const requestId = ++this.ticketFetchRequestId;
      try {
        const ticket = await this.$store.dispatch('getTicket', ticketId);
        if (requestId !== this.ticketFetchRequestId) return;
        if (Number(this.resolvedTicketId) !== Number(ticketId)) return;
        this.selectedTicket = ticket;
      } catch (error) {
        if (requestId !== this.ticketFetchRequestId) return;
        const message = error?.message || this.$t('DEFAULT_ERROR_MESSAGE');
        this.showAlert(message);
        this.closeTicketModal();
      }
    },

    closeTicketModal() {
      this.selectedTicket = null;
      if (this.$route.name === 'crm_ticket') {
        this.$router.push({
          name: 'crm',
          params: { accountId: this.accountId },
        });
      }
    },

    onTicketUpdated(ticket) {
      this.selectedTicket = { ...this.selectedTicket, ...ticket };
    },

    async getTicketsDefault(kanbanId) {
      const requestId = ++this.ticketRequestId;
      const params = {
        kanbanfunnel_id: kanbanId,
        per_page: 10,
        status: this.statusFilter,
        q: this.searchQuery || null,
        date_field: this.dateField,
        agent_ids: this.agentFilter.length > 0 ? this.agentFilter : null,
        start_date: this.startDate || null,
        end_date: this.endDate || null,
        priority: this.priorityFilter.length > 0 ? this.priorityFilter : null,
        label_ids: this.labelFilter.length > 0 ? this.labelFilter : null,
      };

      this.loading = true;
      try {
        const columsTickets = await this.$store.dispatch(
          'getTicketsByKanban',
          { ...params, _requestId: requestId }
        );
        if (requestId !== this.ticketRequestId) return;
        this.kanbanMeta = columsTickets?.kanban_meta;
      } catch (error) {
        if (requestId !== this.ticketRequestId) return;
        const message = error?.message || this.$t('DEFAULT_ERROR_MESSAGE');
        this.showAlert(message);
      } finally {
        if (requestId === this.ticketRequestId) {
          this.loading = false;
        }
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.contacts-page {
  display: flex;
  flex-direction: column;
  flex: 1 1 auto;
  width: 100%;
  height: 100%;
  min-height: 0;
  overflow: hidden;
  background: var(--color-canvas);
}
.loading {
  text-align: center;
}
.left-wrap {
  display: flex;
  flex-direction: column;
  flex: 1 1 auto;
  height: 100%;
  min-height: 0;
  overflow: hidden;
}

.left-wrap::v-deep .header {
  flex-shrink: 0;
}

.left-wrap::v-deep .task-board-wrapper {
  flex: 1 1 auto;
  min-height: 0;
  height: auto;
}
</style>
